import React, { useState, useEffect } from "react";
import ProductCard from "./ProductCard";

export default function SimilarProducts({ currentProductId, category }) {
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper function to get the lowest price from available variants
  const getLowestPrice = (prices) => {
    if (!prices || typeof prices !== 'object') return 0;
    
    const priceValues = Object.values(prices)
      .map(price => Number(price))
      .filter(price => !isNaN(price) && price > 0);
    
    return priceValues.length > 0 ? Math.min(...priceValues) : 0;
  };

  // Helper function to get discounted price
  const getDiscountedPrice = (discountedPrices, offerType) => {
    if (offerType !== "Discount" || !discountedPrices) return null;
    
    const discountValues = Object.values(discountedPrices)
      .map(price => Number(price))
      .filter(price => !isNaN(price) && price > 0);
    
    return discountValues.length > 0 ? Math.min(...discountValues) : null;
  };

  useEffect(() => {
    const fetchSimilarProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/list-products`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Filter products by category and exclude current product
        const filteredProducts = data.products
          .filter(product => product.category === category && product.id !== currentProductId)
          .slice(0, 3) // Limit to 3 products
          .map(product => {
            const originalPrice = getLowestPrice(product.prices);
            const discountedPrice = getDiscountedPrice(product.discountedPrices, product.offerType);
            
            return {
              id: product.id,
              image: product.images && product.images.length > 0 ? product.images[0] : null,
              name: product.name,
              discountedPrice: discountedPrice || originalPrice,
              originalPrice: originalPrice,
              hasDiscount: product.offerType === "Discount" && discountedPrice !== null
            };
          })
          .filter(product => product.originalPrice > 0); // Filter out products with invalid prices
          
        setSimilarProducts(filteredProducts);
      } catch (err) {
        console.error("Failed to fetch similar products:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (category) {
      fetchSimilarProducts();
    }
  }, [category, currentProductId]);

  if (loading) {
    return (
      <div className="mb-12">
        <h2 className="text-2xl text-[#333333] text-center mb-6">
          You May Also Like
        </h2>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-[#D4AF37]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-12">
        <h2 className="text-2xl text-[#333333] text-center mb-6">
          You May Also Like
        </h2>
        <p className="text-center text-red-500">Error loading similar products</p>
      </div>
    );
  }

  if (similarProducts.length === 0) {
    return (
      <div className="mb-12">
        <h2 className="text-2xl text-[#333333] text-center mb-6">
          You May Also Like
        </h2>
        <p className="text-center text-gray-500">No similar products found</p>
      </div>
    );
  }

  return (
    <div className="mb-12">
      <h2 className="text-2xl text-[#333333] text-center mb-6">
        You May Also Like
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        {similarProducts.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </div>
  );
}